// src/pages/LoginPage.js
import React, { useState, useContext, useRef, useEffect } from 'react';
import { AuthContext } from '../App';
import { searchWorkersForLogin, loginAsWorker } from '../services/api';

// The picker searches server-side as the user types. Wait this long after
// the last keystroke before asking, so a name costs one request, not ten.
const DEBOUNCE_MS = 250;
// Below this, the server answers empty — don't bother asking.
const MIN_SEARCH_CHARS = 2;

// Matches the labor board's labels so a worker sees "Project Manager",
// not the raw "pm" stored on the record.
const ROLE_LABELS = {
  carpenter:      'Carpenter',
  laborer:        'Laborer',
  foreman:        'Foreman',
  superintendent: 'Site Superintendent',
  pm:             'Project Manager',
  pe:             'Project Engineer',
  px:             'Project Executive',
  estimator:      'Estimator',
  admin:          'Admin',
  warehouse:      'Warehouse',
  staff:          'Staff',
};

const roleLabel = (role) => ROLE_LABELS[role] || role;

export default function LoginPage() {
  const { login } = useContext(AuthContext);
  const [matches, setMatches]               = useState([]);
  const [searching, setSearching]           = useState(false);
  const [loading, setLoading]               = useState(false);
  const [search, setSearch]                 = useState('');
  const [error, setError]                   = useState('');
  const [step, setStep]                     = useState('search'); // search | password
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [highlight, setHighlight]           = useState(0);
  const [employeeId, setEmployeeId]         = useState('');
  const employeeIdRef = useRef(null);
  // Bumped on every keystroke so a slow response for an old term can't
  // overwrite the results for what the user has since typed.
  const requestSeq = useRef(0);

  useEffect(() => {
    if (step === 'password') employeeIdRef.current?.focus();
  }, [step]);

  // Debounced server-side name search.
  useEffect(() => {
    if (step !== 'search') return;

    const term = search.trim();
    if (term.length < MIN_SEARCH_CHARS) {
      requestSeq.current++;     // invalidate anything still in flight
      setMatches([]);
      setSearching(false);
      return;
    }

    const seq = ++requestSeq.current;
    setSearching(true);

    const timer = setTimeout(() => {
      searchWorkersForLogin(term)
        .then(res => {
          if (seq !== requestSeq.current) return; // a newer search won
          setMatches(res.data.workers || []);
          setHighlight(0);
          setError('');
        })
        .catch(err => {
          if (seq !== requestSeq.current) return;
          const status = err.response?.status;
          setMatches([]);
          setError(
            status
              ? `Server error (${status}). Contact your administrator.`
              : 'Could not reach the NGC Tracker server. Check your connection or try again.'
          );
        })
        .finally(() => {
          if (seq === requestSeq.current) setSearching(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search, step]);

  const handleSelect = (worker) => {
    requestSeq.current++;       // cancel any search still in flight
    setSelectedWorker(worker);
    setSearch('');
    setMatches([]);
    setSearching(false);
    setEmployeeId('');
    setError('');
    setHighlight(0);
    setStep('password');
  };

  const handleSearchKeyDown = (e) => {
    if (!matches.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(matches[highlight] || matches[0]);
    }
  };

  const handleLogin = async () => {
    if (!employeeId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await loginAsWorker(selectedWorker.worker_id, employeeId.trim());
      login(res.data.worker, res.data.token);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError('Incorrect Employee ID. Please try again.');
      } else {
        setError('Login failed. Please try again.');
      }
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep('search');
    setSelectedWorker(null);
    setEmployeeId('');
    setSearch('');
    setMatches([]);
    setError('');
    setHighlight(0);
  };

  const term = search.trim();
  const showPicker = step === 'search' && term.length >= MIN_SEARCH_CHARS;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      {/* Logo */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{
          width: 80, height: 80,
          background: 'var(--ngc-orange)',
          borderRadius: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
          fontSize: '2rem'
        }}>🏗️</div>
        <h1 style={{ color: 'var(--text-primary)', marginBottom: 4 }}>NGC Tracker</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Next Generation Construction
        </p>
      </div>

      {/* Step 1 — Type name, pick from the live list */}
      {step === 'search' && (
        <div style={{ width: '100%', maxWidth: 380 }}>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16, textAlign: 'center' }}>
            Enter your name to sign in
          </p>

          <div className="login-search-wrap">
            <input
              className="input"
              placeholder="Your first or last name..."
              value={search}
              onChange={e => { setSearch(e.target.value); setError(''); }}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
              autoFocus
            />
            {showPicker && (
              <div className="login-picker">
                {matches.length > 0 ? (
                  matches.map((w, i) => (
                    <div
                      key={w.worker_id}
                      className={'login-picker-item' + (i === highlight ? ' is-active' : '')}
                      onMouseDown={e => { e.preventDefault(); handleSelect(w); }}
                      onMouseEnter={() => setHighlight(i)}
                    >
                      <div className="item-row-icon">👤</div>
                      <div className="item-row-info">
                        <h4>{w.display_name}</h4>
                        <p>{roleLabel(w.role)}{w.division_name ? ` · ${w.division_name}` : ''}</p>
                      </div>
                      <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  ))
                ) : searching ? (
                  <div className="login-picker-empty">Searching...</div>
                ) : error ? (
                  <div className="login-picker-empty">{error}</div>
                ) : (
                  <div className="login-picker-empty">No match — try a different spelling.</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Step 2 — Enter Employee ID */}
      {step === 'password' && selectedWorker && (
        <div style={{ width: '100%', maxWidth: 380 }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>👤</div>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: 4 }}>{selectedWorker.display_name}</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 6 }}>
              {roleLabel(selectedWorker.role)}
              {selectedWorker.division_name ? ` · ${selectedWorker.division_name}` : ''}
            </p>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Enter your Employee ID to continue</p>
          </div>
          <input
            ref={employeeIdRef}
            className="input"
            type="password"
            placeholder="Employee ID"
            value={employeeId}
            onChange={e => { setEmployeeId(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{ marginBottom: 12, textAlign: 'center', letterSpacing: '0.15em' }}
          />
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
          <button className="btn btn-primary" onClick={handleLogin}
            disabled={loading || !employeeId.trim()}>
            {loading ? 'Signing in...' : 'Sign In →'}
          </button>
          <button className="btn btn-ghost" style={{ marginTop: 12 }} onClick={goBack}>
            ← Not me
          </button>
        </div>
      )}
    </div>
  );
}
